import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import MainLayout from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  Settings as SettingsIcon,
  Key,
  Check,
  X,
  Building,
  Palette,
  Bot,
  Sparkles,
  Globe,
  Search,
  Youtube,
  ShoppingBag,
  BarChart3,
  Zap,
  Mail,
  MessageSquare,
  Users,
  ArrowRight,
  ExternalLink,
  Loader2,
  CheckCircle2,
  Clock,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const Settings = () => {
  const { api, user } = useAuth();
  const [searchParams] = useSearchParams();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Agency form
  const [formData, setFormData] = useState({
    agency_name: '',
    primary_color: '#6C63FF',
  });

  // Integrations status
  const [integrations, setIntegrations] = useState({
    google_ads: { connected: false },
    meta_ads: { connected: false, in_waitlist: false },
    wordpress: { configured: false },
    fascinads_ai: { status: 'coming_soon', in_waitlist: false }
  });

  // WordPress form
  const [wpForm, setWpForm] = useState({
    url: '',
    builder: 'elementor',
    api_key: '',
    auto_export: false
  });
  const [wpTesting, setWpTesting] = useState(false);
  const [wpSaving, setWpSaving] = useState(false);

  // Modals
  const [showWaitlistModal, setShowWaitlistModal] = useState(false);
  const [waitlistService, setWaitlistService] = useState('');
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [joiningWaitlist, setJoiningWaitlist] = useState(false);

  // Fetch data
  useEffect(() => {
    fetchSettings();
    fetchIntegrationsStatus();
  }, []);

  // Handle OAuth callbacks from URL params
  useEffect(() => {
    // Google Ads callback
    if (searchParams.get('google_ads_connected') === 'true') {
      toast.success('¡Google Ads conectado correctamente!');
      fetchIntegrationsStatus();
      // Clear URL params
      window.history.replaceState({}, '', window.location.pathname);
    } else if (searchParams.get('google_ads_error')) {
      const error = searchParams.get('google_ads_error');
      toast.error(`Error al conectar Google Ads: ${error}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
    
    // Meta Ads callback
    if (searchParams.get('meta_connected') === 'true') {
      const metaUser = searchParams.get('meta_user');
      toast.success(`¡Meta Ads conectado! Usuario: ${metaUser || 'conectado'}`);
      fetchIntegrationsStatus();
      window.history.replaceState({}, '', window.location.pathname);
    } else if (searchParams.get('meta_error')) {
      const error = searchParams.get('meta_error');
      const desc = searchParams.get('meta_error_desc');
      toast.error(`Error al conectar Meta: ${error}${desc ? ` - ${desc}` : ''}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [searchParams]);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/settings');
      setSettings(response.data);
      setFormData({
        agency_name: response.data.agency_name || user?.agency_name || '',
        primary_color: response.data.primary_color || '#6C63FF',
      });
    } catch (error) {
      // Settings endpoint might not exist yet
      console.log('Settings fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchIntegrationsStatus = useCallback(async () => {
    try {
      const response = await api.get('/integrations/status');
      setIntegrations(response.data);
      
      // Populate WordPress form if configured
      if (response.data.wordpress?.configured) {
        setWpForm({
          url: response.data.wordpress.url || '',
          builder: response.data.wordpress.builder || 'elementor',
          api_key: '',
          auto_export: response.data.wordpress.auto_export || false
        });
      }
    } catch (error) {
      console.log('Integrations status error:', error);
    }
  }, [api]);

  const handleSaveAgency = async () => {
    setSaving(true);
    try {
      await api.put('/settings', {
        agency_name: formData.agency_name,
        primary_color: formData.primary_color
      });
      toast.success('Configuración guardada');
      fetchSettings();
    } catch (error) {
      toast.error('Error al guardar configuración');
    } finally {
      setSaving(false);
    }
  };

  // ==================== FASCINADS AI WAITLIST ====================
  
  const handleJoinFascinadsWaitlist = () => {
    setWaitlistService('fascinads_ai');
    setWaitlistEmail(user?.email || '');
    setShowWaitlistModal(true);
  };

  const handleSubmitWaitlist = async () => {
    setJoiningWaitlist(true);
    try {
      await api.post('/integrations/waitlist', {
        service: waitlistService
      });
      toast.success('¡Apuntado! Te avisamos cuando esté listo');
      setShowWaitlistModal(false);
      fetchIntegrationsStatus();
    } catch (error) {
      toast.error('Error al unirse a la lista de espera');
    } finally {
      setJoiningWaitlist(false);
    }
  };

  // ==================== WORDPRESS ====================

  const handleSaveWordpress = async () => {
    if (!wpForm.url) {
      toast.error('Introduce la URL de tu WordPress');
      return;
    }
    
    if (!wpForm.url.startsWith('https://')) {
      toast.error('La URL debe empezar con https://');
      return;
    }

    setWpSaving(true);
    try {
      await api.post('/integrations/wordpress/save', wpForm);
      toast.success('WordPress configurado correctamente');
      fetchIntegrationsStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar configuración');
    } finally {
      setWpSaving(false);
    }
  };

  const handleTestWordpress = async () => {
    if (!wpForm.url) {
      toast.error('Introduce la URL de tu WordPress');
      return;
    }

    setWpTesting(true);
    try {
      const response = await api.post('/integrations/wordpress/test', {
        url: wpForm.url,
        api_key: wpForm.api_key || null
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error('Error al probar conexión');
    } finally {
      setWpTesting(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-brand" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="max-w-5xl space-y-8" data-testid="settings-page">
        {/* Header */}
        <div>
          <h1 className="font-display text-3xl font-bold text-text-main">Configuración</h1>
          <p className="text-text-secondary mt-1">Gestiona las integraciones y ajustes de tu agencia</p>
        </div>

        {/* Agency Settings */}
        <div className="bg-surface border border-border-subtle rounded-xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-brand/10 flex items-center justify-center">
              <Building className="w-5 h-5 text-brand" />
            </div>
            <div>
              <h2 className="font-medium text-text-main">Datos de la Agencia</h2>
              <p className="text-sm text-text-secondary">Información básica de tu agencia</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label className="text-text-secondary">Nombre de la Agencia</Label>
              <Input
                value={formData.agency_name}
                onChange={(e) => setFormData({ ...formData, agency_name: e.target.value })}
                placeholder="Mi Agencia Digital"
                className="bg-app-bg border-border-subtle text-text-main placeholder:text-text-muted"
                data-testid="agency-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-text-secondary">Color Principal</Label>
              <div className="flex gap-3">
                <input
                  type="color"
                  value={formData.primary_color}
                  onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                  className="w-12 h-10 rounded cursor-pointer border-0"
                />
                <Input
                  value={formData.primary_color}
                  onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                  placeholder="#6C63FF"
                  className="bg-app-bg border-border-subtle text-text-main placeholder:text-text-muted flex-1"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end mt-6">
            <Button
              onClick={handleSaveAgency}
              disabled={saving}
              className="bg-brand hover:bg-brand/90"
              data-testid="save-agency-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Guardar cambios
            </Button>
          </div>
        </div>

        {/* Integrations */}
        <div>
          <h2 className="font-display text-xl font-semibold text-text-main mb-4 flex items-center gap-2">
            <Key className="w-5 h-5" />
            Integraciones
          </h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            
            {/* ==================== FASCINADS AUTOMATION AI ==================== */}
            <div className="bg-gradient-to-br from-brand/5 to-purple-500/5 border-2 border-brand/20 rounded-xl p-6 relative overflow-hidden">
              {/* Badge */}
              <div className="absolute top-4 right-4">
                <span className="px-3 py-1 bg-amber-500/20 text-amber-400 text-xs font-medium rounded-full flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  Próximamente
                </span>
              </div>
              
              {/* Header */}
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-brand to-purple-600 flex items-center justify-center flex-shrink-0">
                  <Bot className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-text-main text-lg">Fascinads Automation AI</h3>
                  <p className="text-sm text-text-secondary">La herramienta de automatización de marketing más potente del mercado</p>
                </div>
              </div>
              
              {/* Description */}
              <p className="text-sm text-text-muted mb-4">
                Conecta tu negocio con flujos de automatización inteligentes, CRM avanzado, email marketing y mucho más. Todo integrado con Launch AI.
              </p>
              
              {/* Features */}
              <div className="space-y-2 mb-6">
                {[
                  'CRM inteligente',
                  'Email marketing automatizado',
                  'Funnels de ventas',
                  'WhatsApp Business',
                  'Integración directa con Launch AI'
                ].map((feature) => (
                  <div key={feature} className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-brand" />
                    <span className="text-text-main">{feature}</span>
                  </div>
                ))}
              </div>
              
              {/* Action */}
              {integrations.fascinads_ai?.in_waitlist ? (
                <div className="flex items-center gap-2 text-green-400 text-sm">
                  <Check className="w-4 h-4" />
                  <span>Ya estás en la lista de espera</span>
                </div>
              ) : (
                <Button 
                  onClick={handleJoinFascinadsWaitlist}
                  className="w-full bg-brand hover:bg-brand/90"
                  data-testid="join-fascinads-waitlist-btn"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Unirme a la lista de espera
                </Button>
              )}
            </div>

            {/* ==================== META ADS & GOOGLE ADS - POR PROYECTO ==================== */}
            <div className="bg-surface border border-border-subtle rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500/10 to-green-500/10 flex items-center justify-center">
                  <Zap className="w-6 h-6 text-brand-500" />
                </div>
                <div>
                  <h3 className="font-semibold text-text-main">Meta Ads & Google Ads</h3>
                  <p className="text-sm text-text-secondary">Publicidad integrada por proyecto</p>
                </div>
              </div>
              
              <div className="bg-brand-500/5 border border-brand-500/20 rounded-lg p-4 mb-4">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-brand-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-text-main font-medium">Conexión por proyecto</p>
                    <p className="text-sm text-text-muted mt-1">
                      Las conexiones a Meta Ads y Google Ads ahora se configuran dentro de cada proyecto/funnel, 
                      en el paso de <span className="text-brand-400 font-medium">Campañas</span>. Esto permite 
                      gestionar múltiples clientes con diferentes cuentas publicitarias.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                {[
                  { icon: Users, label: 'Multi-cliente' },
                  { icon: BarChart3, label: 'Cuentas separadas' },
                  { icon: Zap, label: 'Configuración por funnel' },
                  { icon: ArrowRight, label: 'Mayor flexibilidad' }
                ].map((item) => (
                  <div key={item.label} className="flex items-center gap-2 text-sm">
                    <item.icon className="w-4 h-4 text-text-muted" />
                    <span className="text-text-main">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* ==================== WORDPRESS ==================== */}
            <div className="bg-surface border border-border-subtle rounded-xl p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-blue-600/10 flex items-center justify-center">
                    <svg className="w-6 h-6 text-blue-600" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-text-main">WordPress</h3>
                    <p className="text-sm text-text-secondary">Exporta tus landings a WordPress</p>
                  </div>
                </div>
                {integrations.wordpress?.configured ? (
                  <span className="px-2 py-1 bg-brand/20 text-brand text-xs font-medium rounded-full flex items-center gap-1">
                    <Check className="w-3 h-3" />
                    Configurado
                  </span>
                ) : (
                  <span className="px-2 py-1 bg-gray-500/20 text-gray-400 text-xs font-medium rounded-full">
                    Sin configurar
                  </span>
                )}
              </div>
              
              {/* Features */}
              <div className="grid grid-cols-2 gap-2 mb-4">
                {[
                  'Compatible con Elementor',
                  'Compatible con Divi',
                  'HTML limpio y optimizado',
                  'Exportación con un click'
                ].map((feature) => (
                  <div key={feature} className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-blue-500" />
                    <span className="text-text-main">{feature}</span>
                  </div>
                ))}
              </div>
              
              {/* Form */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-text-secondary text-sm">URL de tu WordPress</Label>
                  <Input
                    value={wpForm.url}
                    onChange={(e) => setWpForm({ ...wpForm, url: e.target.value })}
                    placeholder="https://tudominio.com"
                    className="bg-app-bg border-border-subtle text-text-main"
                    data-testid="wordpress-url-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-text-secondary text-sm">Page Builder</Label>
                  <select
                    value={wpForm.builder}
                    onChange={(e) => setWpForm({ ...wpForm, builder: e.target.value })}
                    className="w-full h-10 px-3 rounded-lg bg-app-bg border border-border-subtle text-text-main text-sm"
                    data-testid="wordpress-builder-select"
                  >
                    <option value="elementor">Elementor</option>
                    <option value="divi">Divi</option>
                    <option value="gutenberg">Gutenberg</option>
                    <option value="html">HTML puro</option>
                  </select>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-text-secondary text-sm">API Key de WordPress (opcional)</Label>
                  <Input
                    type="password"
                    value={wpForm.api_key}
                    onChange={(e) => setWpForm({ ...wpForm, api_key: e.target.value })}
                    placeholder="Para exportación automática"
                    className="bg-app-bg border-border-subtle text-text-main"
                    data-testid="wordpress-apikey-input"
                  />
                  <p className="text-xs text-text-muted">
                    Genera tu API key en WordPress → Usuarios → Tu perfil → Application Passwords
                  </p>
                </div>
                
                <div className="flex items-center justify-between py-2">
                  <div>
                    <p className="text-sm text-text-main">Exportación automática al aprobar</p>
                    <p className="text-xs text-text-muted">Cuando apruebes una landing se exportará automáticamente</p>
                  </div>
                  <Switch
                    checked={wpForm.auto_export}
                    onCheckedChange={(checked) => setWpForm({ ...wpForm, auto_export: checked })}
                  />
                </div>
                
                <div className="flex gap-2">
                  <Button
                    onClick={handleSaveWordpress}
                    disabled={wpSaving}
                    className="flex-1 bg-brand hover:bg-brand/90"
                    data-testid="save-wordpress-btn"
                  >
                    {wpSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Guardar
                  </Button>
                  {wpForm.url && (
                    <Button
                      onClick={handleTestWordpress}
                      disabled={wpTesting}
                      variant="outline"
                      data-testid="test-wordpress-btn"
                    >
                      {wpTesting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Probar'}
                    </Button>
                  )}
                </div>
              </div>
            </div>
            
          </div>
        </div>
      </div>

      {/* Waitlist Modal */}
      <Dialog open={showWaitlistModal} onOpenChange={setShowWaitlistModal}>
        <DialogContent className="bg-surface border-border-subtle max-w-md">
          <DialogHeader>
            <DialogTitle className="text-text-main">
              Unirse a la lista de espera
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <p className="text-sm text-text-muted">
              Te enviaremos un email cuando Fascinads Automation AI esté disponible.
            </p>
            
            <div className="space-y-2">
              <Label className="text-text-secondary text-sm">Tu email</Label>
              <Input
                type="email"
                value={waitlistEmail}
                onChange={(e) => setWaitlistEmail(e.target.value)}
                placeholder="tu@email.com"
                className="bg-app-bg border-border-subtle text-text-main"
                disabled
              />
            </div>
            
            <Button 
              onClick={handleSubmitWaitlist}
              disabled={joiningWaitlist}
              className="w-full bg-brand hover:bg-brand/90"
              data-testid="confirm-waitlist-btn"
            >
              {joiningWaitlist ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Sparkles className="w-4 h-4 mr-2" />
              )}
              Confirmar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
};

export default Settings;
